/**
 * WebAssembly Serial Validator
 * Valida seriales contra keys usando WebAssembly
 */

class WasmValidator {
  constructor() {
    this.wasmModule = null;
    this.memory = null;
    this.initialized = false;
  }

  /**
   * Inicializa el módulo WASM
   */
  async init() {
    if (this.initialized) return;

    try {
      // Crear memoria compartida (1 página = 64KB)
      this.memory = new WebAssembly.Memory({ initial: 1 });

      // Cargar el módulo WASM
      const response = await fetch('validator.wasm');
      const buffer = await response.arrayBuffer();
      
      const wasmModule = await WebAssembly.instantiate(buffer, {
        js: {
          mem: this.memory
        }
      });

      this.wasmModule = wasmModule.instance.exports;
      this.initialized = true;
      console.log('WASM Validator initialized successfully');
    } catch (error) {
      console.error('Failed to initialize WASM:', error);
      // Fallback a validación JavaScript si WASM falla
      this.useFallback = true;
    }
  }

  /**
   * Escribe una cadena en la memoria WASM
   */
  writeString(str, offset) {
    const encoder = new TextEncoder();
    const bytes = encoder.encode(str);
    const memory = new Uint8Array(this.memory.buffer);
    memory.set(bytes, offset);
    return bytes.length;
  }

  /**
   * Genera un serial a partir de un key
   */
  async generateSerial(key) {
    if (!this.initialized) await this.init();

    if (this.useFallback) {
      return this.generateSerialJS(key);
    }

    // Escribir key en memoria WASM en offset 0
    const keyLength = this.writeString(key, 0);

    // Llamar a la función WASM
    const hashValue = this.wasmModule.generateSerial(0, keyLength);

    // Convertir hash a string hexadecimal
    const serial = this.hashToSerial(hashValue);
    return serial;
  }

  /**
   * Valida un serial contra un key
   */
  async validateSerial(key, serial) {
    if (!this.initialized) await this.init();

    if (this.useFallback) {
      return this.validateSerialJS(key, serial);
    }

    try {
      // Escribir key en memoria WASM en offset 0
      const keyLength = this.writeString(key, 0);

      // Convertir serial a número
      const serialNumber = parseInt(serial);

      // Llamar a la función WASM que valida el serial como número
      const result = this.wasmModule.validateSerialNumber(0, keyLength, serialNumber);

      return result === 1;
    } catch (error) {
      console.error('WASM validation error:', error);
      return this.validateSerialJS(key, serial);
    }
  }

  /**
   * Convierte un hash numérico a un serial formateado
   */
  hashToSerial(hash) {
    // Convertir a hexadecimal sin signo
    const hex = (hash >>> 0).toString(16).toUpperCase().padStart(8, '0');
    
    // Formato: XXXX-XXXX
    return `${hex.slice(0, 4)}-${hex.slice(4, 8)}`;
  }

  /**
   * Convierte un serial formateado a hash numérico
   */
  serialToHash(serial) {
    const cleaned = serial.replace(/-/g, '');
    return parseInt(cleaned, 16);
  }

  // ============================================
  // Fallback JavaScript (si WASM no está disponible)
  // ============================================

  /**
   * Hash simple de string (implementación JavaScript)
   */
  hashStringJS(str) {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) + hash) + char;
      hash = hash & hash; // Convertir a 32-bit integer
    }
    return hash;
  }

  /**
   * Genera serial usando JavaScript puro
   */
  generateSerialJS(key) {
    const keyHash = this.hashStringJS(key);
    const serialHash = ((keyHash * 7919) ^ 0xDEADBEEF) >>> 0;
    return this.hashToSerial(serialHash);
  }

  /**
   * Valida serial usando JavaScript puro
   */
  validateSerialJS(key, serial) {
    // El serial debe ser exactamente el hash generado del key
    const keyHash = this.hashStringJS(key);
    const expectedHash = ((keyHash * 7919) ^ 0xDEADBEEF) >>> 0;
    
    // Comparar directamente el serial numérico
    return parseInt(serial) === expectedHash;
  }
}

// Exportar instancia singleton
const validator = new WasmValidator();

export default validator;
